import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        origin_url: window.location.origin,
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="forgot-password-page">
      <Navbar onRegisterClick={() => {}} />

      <main className="flex-1 pt-28 pb-16 px-6 flex items-center justify-center">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-8 transition-colors"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft size={18} />
            {language === 'it' ? 'Torna al login' : 'Back to login'}
          </button>

          <div className="text-center mb-8">
            <h1 className="font-heading font-bold text-2xl lg:text-3xl text-[#E5E5E5] mb-2">
              {language === 'it' ? 'Password dimenticata?' : 'Forgot password?'}
            </h1>
            <p className="text-[#A3A3A3]">
              {language === 'it'
                ? 'Inserisci la tua email e ti invieremo un link per reimpostare la password.'
                : 'Enter your email and we will send you a link to reset your password.'}
            </p>
          </div>

          <div className="bg-[#0d1117] border border-[#1f2937] rounded-md p-8">
            {submitted ? (
              <div className="text-center" data-testid="forgot-password-success">
                <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37] flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-[#D4AF37]" />
                </div>
                <h2 className="text-lg font-semibold text-[#E5E5E5] mb-3">
                  {language === 'it' ? 'Controlla la tua email' : 'Check your inbox'}
                </h2>
                <p className="text-sm text-[#A3A3A3] mb-2">
                  {language === 'it'
                    ? "Se l'indirizzo è associato a un account BOND, riceverai a breve un'email con il link per reimpostare la password."
                    : 'If the address is associated with a BOND account, you will receive an email with the reset link shortly.'}
                </p>
                <p className="text-xs text-[#737373] mb-6">
                  {language === 'it'
                    ? 'Il link sarà valido per 60 minuti.'
                    : 'The link will be valid for 60 minutes.'}
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black h-12 rounded-none font-medium"
                  data-testid="back-to-login-success-btn"
                >
                  {language === 'it' ? 'TORNA AL LOGIN' : 'BACK TO LOGIN'}
                </Button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-6 p-4 bg-red-900/20 border border-red-900/50 rounded flex items-center gap-3 text-red-400" data-testid="forgot-password-error">
                    <AlertCircle size={20} />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label className="block text-sm text-[#A3A3A3] mb-2">Email</label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="email@esempio.it"
                        className="bg-[#050505] border-[#1f2937] text-[#E5E5E5] h-12 rounded-none pl-12 focus:border-[#D4AF37]"
                        required
                        data-testid="forgot-password-email"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black h-12 rounded-none font-medium"
                    data-testid="forgot-password-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        {language === 'it' ? 'Invio in corso...' : 'Sending...'}
                      </>
                    ) : (
                      language === 'it' ? 'INVIA LINK DI RESET' : 'SEND RESET LINK'
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#1f2937] text-center">
                  <p className="text-sm text-[#A3A3A3]">
                    {language === 'it' ? 'Ti sei ricordato la password?' : 'Remembered your password?'}{' '}
                    <button
                      onClick={() => navigate('/login')}
                      className="text-[#D4AF37] hover:underline"
                      data-testid="go-to-login-link"
                    >
                      {language === 'it' ? 'Accedi' : 'Sign in'}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
